import React, { useState, useEffect, useRef } from "react";
import apiClient from "../../../api/apiClient";
import Loading from "../../../components/Loading/Loading";
import { useToast } from "../../../context/toastContext";
import { useConfirm } from "../../../context/confirmContext";
import { downloadFromApi } from "../../../utils/downloadBlob";

const PLATFORM_ROLES = ["user", "admin", "superadmin"]; 

const AdminManagement = ({ userProfile }) => {
    const toast = useToast();
    const confirm = useConfirm();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [expandedUsers, setExpandedUsers] = useState(new Set());
    const tableRef = useRef(null);
    const [colWidths, setColWidths] = useState([20, 20, 13, 9, 12, 16, 10]);

    const [showCreateAdmin, setShowCreateAdmin] = useState(false);
    const [adminForm, setAdminForm] = useState({ firstName: "", lastName: "", email: "", phoneNumber: "" });
    const [adminSubmitting, setAdminSubmitting] = useState(false);
    const [adminMsg, setAdminMsg] = useState({ type: "", text: "" });
    const [rowLoading, setRowLoading] = useState({});
    
    const fetchUsers = async () => {
        setLoading(true); 
        try {
            const params = new URLSearchParams({ adminOnly: "true" });
            if (search) params.set("search", search);
            const res = await apiClient.get(`/admin/users?${params.toString()}`);
            setUsers(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchUsers(); }, []);

    const handleSearch = (e) => {
        if (e.key === "Enter") fetchUsers();
    };

    const toggleExpand = (userId) => {
        setExpandedUsers((prev) => {
            const next = new Set(prev);
            next.has(userId) ? next.delete(userId) : next.add(userId);
            return next;
        });
    };

    const handleToggleStatus = async (user) => {
        const action = user.enabled ? "deactivate" : "activate";
        const label = action === "activate" ? "Enable" : "Disable";
        const ok = await confirm({
            title: `${label} Admin`,
            message: `${label} ${user.firstName} ${user.lastName}?`,
            confirmLabel: label,
            variant: action === "deactivate" ? "danger" : "primary",
        });
        if (!ok) return;
        setRowLoading((prev) => ({ ...prev, [user.userId]: true }));
        try {
            await apiClient.put(`/admin/users/${user.userId}/status`, { action });
            setUsers((prev) => prev.map((u) => u.userId === user.userId ? { ...u, enabled: !u.enabled } : u));
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to update status");
        } finally {
            setRowLoading((prev) => ({ ...prev, [user.userId]: false }));
        }
    };

    const handlePlatformRoleChange = async (userId, newRole) => {
        const ok = await confirm({
            title: "Change Platform Role",
            message: `Change platform role to "${newRole}"?`,
            confirmLabel: "Change Role",
            variant: "warning",
        });
        if (!ok) return;
        setRowLoading((prev) => ({ ...prev, [userId]: true }));
        try {
            await apiClient.put(`/admin/users/${userId}/platform-role`, { platform_role: newRole });
            setUsers((prev) => prev.map((u) => u.userId === userId ? { ...u, platform_role: newRole } : u));
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to update role");
        } finally {
            setRowLoading((prev) => ({ ...prev, [userId]: false }));
        }
    };

    const handleCreateAdmin = async (e) => {
        e.preventDefault();
        setAdminMsg({ type: "", text: "" });
        setAdminSubmitting(true);
        try {
            await apiClient.post("/admin/accounts/create-admin", adminForm);
            setAdminMsg({ type: "success", text: "Admin account created. Welcome email sent." });
            setAdminForm({ firstName: "", lastName: "", email: "", phoneNumber: "" });
            fetchUsers();
        } catch (err) {
            setAdminMsg({ type: "error", text: err.response?.data?.error || "Failed to create admin" });
        } finally {
            setAdminSubmitting(false);
        }
    };

    const startResize = (index) => (e) => {
        e.preventDefault();
        const startX = e.clientX;
        const startWidths = [...colWidths];
        const tableWidth = tableRef.current?.offsetWidth || 1000;
        document.body.style.userSelect = "none";
        document.body.style.cursor = "col-resize";
        const onMove = (moveE) => {
            const delta = ((moveE.clientX - startX) / tableWidth) * 100;
            const next = [...startWidths];
            next[index] = Math.max(4, startWidths[index] + delta);
            next[index + 1] = Math.max(4, startWidths[index + 1] - delta);
            setColWidths(next);
        };
        const onUp = () => {
            document.body.style.userSelect = "";
            document.body.style.cursor = "";
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
        };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
    };

    const formatPhone = (phone) => {
        if (!phone) return "—";
        if (phone.startsWith("+1") && phone.length === 12)
            return `+1 (${phone.slice(2, 5)}) ${phone.slice(5, 8)}-${phone.slice(8)}`;
        return phone;
    };

    const handleResendInvite = async (user) => {
        try {
            const res = await apiClient.post(`/admin/users/${user.userId}/resend-invite`);
            toast.success(res.data.emailSent ? "Invite resent." : "Temp password reset, but the email failed to send.");
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to resend invite");
        }
    };

    const handleExportCsv = async () => {
        try {
            const params = new URLSearchParams({ adminOnly: "true" });
            if (search) params.set("search", search);
            await downloadFromApi(`/admin/users/export.csv?${params.toString()}`, `dyc-admins-${new Date().toISOString().slice(0, 10)}.csv`);
        } catch (err) {
            toast.error("Failed to export admins");
        }
    };

    const HEADERS = ["Name", "Email", "Phone", "Status", "Platform Role", "Licenses", "Action"];

    return (
        <div>
            {/* Toolbar */}
            <div className="admin-search-bar">
                <input
                    className="admin-search-input"
                    placeholder="Search name, email…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearch}
                />
                <span className="admin-search-count">{users.length} admin{users.length !== 1 ? "s" : ""}</span>
                <button className="admin-action-btn" onClick={handleExportCsv}>Export CSV</button>
                {userProfile === "superadmin" && (
                    <button className="admin-action-btn superadmin-only" onClick={() => setShowCreateAdmin(true)}>
                        + Create Admin
                    </button>
                )}
            </div>

            {/* Create Admin modal */}
            {showCreateAdmin && (
                <div style={{ padding: "var(--space-5)", borderBottom: "1px solid var(--color-border)", background: "var(--color-bg-secondary)" }}>
                    <h3 className="admin-create-title">
                        Create Admin Account <span className="admin-role-tag superadmin">Superadmin</span>
                    </h3>
                    <form onSubmit={handleCreateAdmin}>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label className="admin-form-label">First Name</label>
                                <input className="admin-form-input" required value={adminForm.firstName} onChange={(e) => setAdminForm((p) => ({ ...p, firstName: e.target.value }))} />
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-form-label">Last Name</label>
                                <input className="admin-form-input" required value={adminForm.lastName} onChange={(e) => setAdminForm((p) => ({ ...p, lastName: e.target.value }))} />
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-form-label">Email</label>
                                <input className="admin-form-input" type="email" required value={adminForm.email} onChange={(e) => setAdminForm((p) => ({ ...p, email: e.target.value }))} />
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-form-label">Phone</label>
                                <input className="admin-form-input" required value={adminForm.phoneNumber} onChange={(e) => setAdminForm((p) => ({ ...p, phoneNumber: e.target.value }))} />
                            </div>
                        </div>
                        {adminMsg.text && (
                            <p className={adminMsg.type === "error" ? "admin-form-error" : "admin-form-success"}>{adminMsg.text}</p>
                        )}
                        <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-3)" }}>
                            <button type="submit" className="admin-action-btn primary" disabled={adminSubmitting}>
                                {adminSubmitting ? "Creating…" : "Create Admin"}
                            </button>
                            <button type="button" className="admin-action-btn" style={{ background: "var(--color-border)", color: "var(--color-text-primary)" }} onClick={() => { setShowCreateAdmin(false); setAdminMsg({ type: "", text: "" }); }}>
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Table */}
            {loading ? (
                <div className="admin-loading-wrapper"><Loading /></div>
            ) : (
                <div className="admin-table-scroll" ref={tableRef}>
                    <table className="admin-table">
                        <thead>
                            <tr>
                                {HEADERS.map((label, i) => (
                                    <th key={label} style={{ width: `${colWidths[i]}%`, position: "relative" }}>
                                        {label}
                                        {i < HEADERS.length - 1 && (
                                            <div className="col-resize-handle" onMouseDown={startResize(i)} />
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {users.length === 0 && (
                                <tr><td colSpan={HEADERS.length} className="admin-table-empty">No admin accounts found.</td></tr>
                            )}
                            {users.map((user) => {
                                const isDisabled = !user.enabled;
                                const isExpanded = expandedUsers.has(user.userId);
                                const hasLicenses = user.licenses?.length > 0;
                                return (
                                    <React.Fragment key={user.userId}>
                                        {/* ── Admin user row ── */}
                                        <tr className={isDisabled ? "admin-row-disabled" : ""}>
                                            <td className="admin-td-name">
                                                {hasLicenses && (
                                                    <span className="admin-collapse-toggle" onClick={() => toggleExpand(user.userId)}>
                                                        {isExpanded ? "▼" : "▶"}
                                                    </span>
                                                )}
                                                {user.firstName} {user.lastName}
                                                {isDisabled && <span className="admin-disabled-badge">Disabled</span>}
                                            </td>
                                            <td className="admin-td-secondary">{user.email}</td>
                                            <td className="admin-td-secondary">{formatPhone(user.phoneNumber)}</td>
                                            <td className={isDisabled ? "admin-status-disabled" : "admin-status-active"}>
                                                {isDisabled ? "Disabled" : "Active"}
                                            </td>
                                            <td>
                                                {userProfile === "superadmin" ? (
                                                    <select
                                                        className="admin-form-input"
                                                        style={{ padding: "2px 6px", fontSize: "var(--text-xs)" }}
                                                        value={user.platform_role || "admin"}
                                                        onChange={(e) => handlePlatformRoleChange(user.userId, e.target.value)}
                                                        disabled={!!rowLoading[user.userId]}
                                                    >
                                                        {PLATFORM_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                                                    </select>
                                                ) : (
                                                    <span className={`admin-role-tag ${user.platform_role === "superadmin" ? "superadmin" : ""}`}>
                                                        {user.platform_role || "admin"}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="admin-td-secondary">
                                                {hasLicenses ? (
                                                    <span className="admin-license-expand-trigger" onClick={() => toggleExpand(user.userId)}>
                                                        {user.licenses.length} license{user.licenses.length !== 1 ? "s" : ""}
                                                        <span className="admin-expand-chevron">{isExpanded ? " ▲" : " ▼"}</span>
                                                    </span>
                                                ) : (
                                                    <span className="admin-table-none">No licenses</span>
                                                )}
                                            </td>
                                            <td className="admin-td-action">
                                                <div style={{ display: "flex", gap: "var(--space-2)" }}>
                                                    <button
                                                        className={`admin-action-btn ${isDisabled ? "enable" : "disable"}`}
                                                        onClick={() => handleToggleStatus(user)}
                                                        disabled={!!rowLoading[user.userId]}
                                                    >
                                                        {rowLoading[user.userId] ? "Updating…" : isDisabled ? "Enable" : "Disable"}
                                                    </button>
                                                    {user.userStatus === "FORCE_CHANGE_PASSWORD" && (
                                                        <button className="admin-action-btn" onClick={() => handleResendInvite(user)}>
                                                            Resend Invite
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>

                                        {/* ── License detail panel ── */}
                                        {isExpanded && hasLicenses && (
                                            <tr className="admin-detail-row">
                                                <td colSpan={HEADERS.length} className="admin-detail-cell">
                                                    <div className="admin-license-detail-panel">
                                                        {user.licenses.map((l) => (
                                                            <div key={l.licenseNo} className="admin-license-detail-card">
                                                                <div className="admin-license-detail-company">{l.company}</div>
                                                                <div className="admin-license-detail-row">
                                                                    <span className="admin-license-detail-no">{l.licenseNo}</span>
                                                                    <span className="admin-role-tag">{l.userRole}</span>
                                                                </div>
                                                                {(l.industry || l.profile) && (
                                                                    <div className="admin-license-detail-meta">
                                                                        {[l.industry, l.profile].filter(Boolean).join(" · ")}
                                                                    </div>
                                                                )}
                                                                {l.city && (
                                                                    <div className="admin-license-detail-meta">{l.city}, {l.state}</div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminManagement;
